import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react"
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  FiCheck,
  FiDatabase,
  FiEdit2,
  FiFileText,
  FiGitBranch,
  FiInfo,
  FiLayers,
  FiPlus,
  FiPrinter,
  FiShare2,
  FiX,
} from "react-icons/fi"
import { PiBroom } from "react-icons/pi"
import Select from "@/components/Common/AppSelect"
import { previewDocumentPdf } from "../../../api/documentPdf"
import {
  type MeldunekCreateDTO,
  type MeldunekListResponseDTO,
  type MeldunekResponseDTO,
  type NewProductionResponse,
  NewProductionService,
  type ProductionOrderResponseDTO,
  ProductionOrderService,
  type ProductResponceDTO,
  ProductService,
  type TechnologyListResponseDTO,
  type TechnologyResponseDTO,
  TechnologyService,
  WareHouseService,
  type WarehousesDTO,
} from "../../../client"
import { PaginationFooter } from "../../Common/PaginationFooter"
import UnifiedProductPickerModal from "../Modals/UnifiedProductPickerModal"
import WarehouseLocationSelect from "../Modals/WarehouseLocationSelect"
import PrintLanguageSelect from "../shared/PrintLanguageSelect"
import {
  formatNumber,
  generateLabelHtml,
  generateSimpleMeldunekLabelHtml,
  getLabelStyles,
  getSimpleLabelStyles,
} from "./labelUtils"
import ProductionMeldunekCreateModal from "./ProductionMeldunekCreateModal"

type Props = {
  id: string
}

type MaterialAllocationDraft = {
  id: string
  locationCode: string
  warehouseLotId?: string
  lotLabel?: string
}

const MELDUNKI_PAGE_SIZE = 10

export default function ProductionOrderPage({ id }: Props) {
  const toast = useToast()
  const qc = useQueryClient()
  const router = useRouter()
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const statBlueBg = useColorModeValue("blue.50", "blue.900")
  const statGreenBg = useColorModeValue("green.50", "green.900")
  const statOrangeBg = useColorModeValue("orange.50", "orange.900")
  const editPanelBg = useColorModeValue("blue.50", "gray.700")
  const editPanelBorder = useColorModeValue("blue.200", "blue.500")
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700")
  const infoMeldunekBg = useColorModeValue("blue.50", "blue.900")
  const infoMeldunekBorder = useColorModeValue("blue.200", "blue.500")
  const mutedTextColor = useColorModeValue("gray.500", "gray.400")
  const headingColor = useColorModeValue("gray.800", "whiteAlpha.900")
  const editHeadingColor = useColorModeValue("blue.700", "blue.300")
  const formFieldBg = useColorModeValue("white", "gray.800")
  const stickyCellBg = useColorModeValue("white", "gray.800")

  const { isOpen, onOpen, onClose } = useDisclosure()
  const createMeldunekModal = useDisclosure()
  const deleteDialog = useDisclosure()
  const techChangeDialog = useDisclosure()
  const materialLocationPicker = useDisclosure()
  const materialsModal = useDisclosure()
  const techChangeCancelRef = useRef<HTMLButtonElement | null>(null)
  const [pickerProductId, setPickerProductId] = useState<string | null>(null)
  const [pickerAllocationId, setPickerAllocationId] = useState<string | null>(
    null,
  )

  const {
    data: order,
    isPending,
    isError,
    error,
  } = useQuery<ProductionOrderResponseDTO>({
    queryKey: ["production-orders", "detail", id],
    queryFn: () => ProductionOrderService.getProductionOrderByID(id),
  })

  const { data: technology } = useQuery<TechnologyResponseDTO>({
    queryKey: ["technology", "detail", order?.technology_id],
    queryFn: () => TechnologyService.getTechnologyByID(order!.technology_id),
    enabled: !!order?.technology_id,
  })

  const { data: technologiesResponse } = useQuery<TechnologyListResponseDTO>({
    queryKey: ["technologies", "list"],
    queryFn: () =>
      TechnologyService.getTechnologyListPage(undefined, "all", 1, 1000),
  })
  const technologies = useMemo(() => {
    const items = (technologiesResponse?.items ?? []).filter(
      (technology) =>
        (technology.is_active && !technology.is_archived) ||
        technology.id === order?.technology_id,
    )
    return items
  }, [order?.technology_id, technologiesResponse?.items])

  const [meldunkiPage, setMeldunkiPage] = useState(1)

  const { data: meldunkiResponse, isPlaceholderData: isMeldunkiPlaceholder } =
    useQuery<MeldunekListResponseDTO>({
      queryKey: ["production-orders", "meldunki", id, meldunkiPage],
      queryFn: () =>
        ProductionOrderService.getMeldunkiByOrder(
          id,
          meldunkiPage,
          MELDUNKI_PAGE_SIZE,
        ),
      placeholderData: keepPreviousData,
    })
  const meldunki = meldunkiResponse?.items ?? []
  const meldunkiTotal = meldunkiResponse?.total ?? 0
  const meldunkiTotalPages = Math.max(
    1,
    Math.ceil(meldunkiTotal / MELDUNKI_PAGE_SIZE),
  )

  const { data: warehouses } = useQuery<WarehousesDTO[]>({
    queryKey: ["wms", "warehouses", "list"],
    queryFn: () => WareHouseService.getAllWarehouses(),
  })

  const { data: activeProduction } = useQuery<NewProductionResponse>({
    queryKey: ["new-production", "active", order?.machine_id],
    queryFn: () =>
      NewProductionService.getActive({ machine_id: order?.machine_id ?? "" }),
    enabled: !!order?.machine_id,
  })

  const productIds = useMemo(() => {
    const ids = new Set<string>()
    for (const input of technology?.inputs ?? []) {
      if (input.product_id) ids.add(input.product_id)
      for (const sub of input.substitutes ?? []) {
        if (sub.substitute_product_id) ids.add(sub.substitute_product_id)
      }
    }
    for (const output of technology?.outputs ?? []) {
      if (output.product_id) ids.add(output.product_id)
    }
    for (const meldunek of meldunki ?? []) {
      if (meldunek.output_product_id) ids.add(meldunek.output_product_id)
      for (const output of meldunek.outputs ?? []) {
        if (output.product_id) ids.add(output.product_id)
      }
      for (const item of meldunek.items ?? []) {
        if (item.product_id) ids.add(item.product_id)
      }
    }
    return Array.from(ids)
  }, [technology, meldunki])

  const productQueries = useQueries({
    queries: productIds.map((productId) => ({
      queryKey: ["products", "detail", productId],
      queryFn: () => ProductService.getProductByID(productId),
      enabled: !!productId,
    })),
  })

  const productMap = useMemo(() => {
    const map = new Map<string, ProductResponceDTO>()
    for (const result of productQueries) {
      const product = result.data
      if (product?.id) {
        map.set(product.id, product)
      }
    }
    return map
  }, [productQueries])

  const warehouseMap = useMemo(() => {
    const map = new Map<string, WarehousesDTO>()
    ;(warehouses ?? []).forEach((w) => {
      map.set(w.id, w)
    })
    return map
  }, [warehouses])

  const mainOutput = useMemo(() => {
    if (!technology || !technology.outputs || technology.outputs.length === 0)
      return null
    return technology.outputs.find((o) => o.is_main) ?? technology.outputs[0]
  }, [technology])

  const [producedQty, setProducedQty] = useState("")
  const [note, setNote] = useState("")
  const [scrapMap, setScrapMap] = useState<Record<string, string>>({})
  const [inputAllocationsMap, setInputAllocationsMap] = useState<
    Record<string, MaterialAllocationDraft[]>
  >({})
  const [selectedProductPerSlot, setSelectedProductPerSlot] = useState<
    Record<string, string>
  >({})
  const [outputLocationCode, setOutputLocationCode] = useState("")
  const [infoMeldunek, setInfoMeldunek] = useState<MeldunekResponseDTO | null>(
    null,
  )
  const [editMeldunek, setEditMeldunek] = useState<MeldunekResponseDTO | null>(
    null,
  )
  const [editMode, setEditMode] = useState(false)
  const [statusDraft, setStatusDraft] = useState("")
  const [targetQtyDraft, setTargetQtyDraft] = useState("")
  const [technologyDraft, setTechnologyDraft] = useState("")
  const [startDateDraft, setStartDateDraft] = useState("")
  const [plannedEndDateDraft, setPlannedEndDateDraft] = useState("")
  const [pendingOrderUpdate, setPendingOrderUpdate] = useState<{
    status?: string
    target_qty?: number
    technology_id?: string
    start_date?: string
    planned_end_date?: string
  } | null>(null)
  const [selectedMeldunkiMap, setSelectedMeldunkiMap] = useState<
    Record<string, MeldunekResponseDTO>
  >({})

  const createAllocationDraft = useCallback(
    (
      overrides: Partial<MaterialAllocationDraft> = {},
    ): MaterialAllocationDraft => ({
      id:
        overrides.id ??
        `alloc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      locationCode: overrides.locationCode ?? "",
      warehouseLotId: overrides.warehouseLotId,
      lotLabel: overrides.lotLabel,
    }),
    [],
  )

  const getDefaultAllocationId = useCallback(
    (productId: string) => `alloc-default-${productId}`,
    [],
  )

  const getInputAllocations = useCallback(
    (productId: string) => {
      const allocations = inputAllocationsMap[productId] ?? []
      return allocations.length > 0
        ? allocations
        : [createAllocationDraft({ id: getDefaultAllocationId(productId) })]
    },
    [createAllocationDraft, getDefaultAllocationId, inputAllocationsMap],
  )

  const updateInputAllocation = useCallback(
    (
      productId: string,
      allocationId: string,
      updater: (allocation: MaterialAllocationDraft) => MaterialAllocationDraft,
    ) => {
      setInputAllocationsMap((prev) => {
        const current = prev[productId] ?? [
          createAllocationDraft({ id: allocationId }),
        ]
        return {
          ...prev,
          [productId]: current.map((allocation) =>
            allocation.id === allocationId ? updater(allocation) : allocation,
          ),
        }
      })
    },
    [createAllocationDraft],
  )

  const addInputAllocation = useCallback(
    (productId: string) => {
      setInputAllocationsMap((prev) => {
        const current = prev[productId] ?? [
          createAllocationDraft({ id: getDefaultAllocationId(productId) }),
        ]
        return {
          ...prev,
          [productId]: [...current, createAllocationDraft()],
        }
      })
    },
    [createAllocationDraft, getDefaultAllocationId],
  )

  const removeInputAllocation = useCallback(
    (productId: string, allocationId: string) => {
      setInputAllocationsMap((prev) => {
        const current = prev[productId] ?? []
        const next = current.filter(
          (allocation) => allocation.id !== allocationId,
        )
        return {
          ...prev,
          [productId]:
            next.length > 0
              ? next
              : [
                  createAllocationDraft({
                    id: getDefaultAllocationId(productId),
                  }),
                ],
        }
      })
    },
    [createAllocationDraft, getDefaultAllocationId],
  )

  const remaining = useMemo(() => {
    if (!order || !mainOutput) return 0
    // remaining in runs: (target_qty - produced_qty) / mainOutput.qty
    const remainingProduct = Math.max(order.target_qty - order.produced_qty, 0)
    return mainOutput.qty > 0 ? remainingProduct / mainOutput.qty : 0
  }, [order, mainOutput])
  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString() : "—"
  const fmtMoney = (value?: number | null) =>
    typeof value === "number" && Number.isFinite(value)
      ? new Intl.NumberFormat("pl-PL", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value)
      : "—"
  const toDateInputValue = useCallback((value?: string | null) => {
    if (!value) return ""
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""
    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, "0")
    const day = `${date.getDate()}`.padStart(2, "0")
    return `${year}-${month}-${day}`
  }, [])

  // plannedInputs: runCount is directly the factor (number of technology runs)
  const plannedInputs = useMemo(() => {
    if (!technology || !mainOutput) return []
    const runCount = Number.parseFloat(producedQty)
    if (!Number.isFinite(runCount) || runCount <= 0) return []
    const factor = runCount
    return (technology.inputs ?? []).map((inp) => {
      const chosenProductId = selectedProductPerSlot[inp.id] ?? inp.product_id
      let qty = inp.qty
      let lossPercent = inp.loss_percent ?? 0
      if (chosenProductId !== inp.product_id) {
        const sub = (inp.substitutes ?? []).find(
          (s) => s.substitute_product_id === chosenProductId,
        )
        if (sub) {
          qty = sub.qty
          lossPercent = sub.loss_percent ?? 0
        }
      }
      const lossFactor = 1 + lossPercent / 100
      const planned = qty * factor * lossFactor
      const scrap = Number.parseFloat(scrapMap[chosenProductId] ?? "0") || 0
      return {
        slot_id: inp.id,
        product_id: chosenProductId,
        planned_qty: planned,
        scrap_qty: scrap,
        used_qty: planned + scrap,
      }
    })
  }, [technology, mainOutput, producedQty, scrapMap, selectedProductPerSlot])

  // plannedOutputs: each output.qty * runCount
  const plannedOutputs = useMemo(() => {
    if (!technology || !mainOutput) return []
    const runCount = Number.parseFloat(producedQty)
    if (!Number.isFinite(runCount) || runCount <= 0) return []
    const factor = runCount
    return (technology.outputs ?? []).map((output) => ({
      product_id: output.product_id,
      qty_produced: output.qty * factor,
      is_main: output.product_id === mainOutput.product_id || output.is_main,
    }))
  }, [technology, mainOutput, producedQty])

  const formatMeldunekOutputs = (meldunek: MeldunekResponseDTO) => {
    const outputs =
      (meldunek.outputs ?? []).length > 0
        ? meldunek.outputs
        : [
            {
              product_id: meldunek.output_product_id,
              qty_produced: meldunek.produced_qty,
              is_main: true,
            },
          ]
    return outputs
      .map((output) => {
        const product = productMap.get(output.product_id)
        return `${product?.name ?? output.product_id}: ${formatNumber(output.qty_produced)}${output.is_main ? " *" : ""}`
      })
      .join(", ")
  }

  const canCreateMeldunek = useMemo(() => {
    const runCount = Number.parseFloat(producedQty)
    if (!Number.isFinite(runCount) || runCount <= 0) return false
    if (remaining <= 0) return false
    if (order && runCount > remaining) return false
    if (!outputLocationCode.trim()) return false
    const missingInputLocation = (technology?.inputs ?? []).some((inp) => {
      const chosenProductId = selectedProductPerSlot[inp.id] ?? inp.product_id
      return (
        !productMap.get(chosenProductId)?.is_service &&
        !getInputAllocations(chosenProductId).some((allocation) =>
          allocation.locationCode.trim(),
        )
      )
    })
    if (missingInputLocation) return false
    return true
  }, [
    producedQty,
    order,
    remaining,
    outputLocationCode,
    technology,
    getInputAllocations,
    productMap,
    selectedProductPerSlot,
  ])

  const buildMeldunekItems = useCallback(
    (scrapByProduct: Record<string, number>) =>
      (technology?.inputs ?? []).flatMap((inp) => {
        const chosenProductId = selectedProductPerSlot[inp.id] ?? inp.product_id
        const scrapQty = scrapByProduct[chosenProductId] ?? 0
        if (productMap.get(chosenProductId)?.is_service) {
          return [
            {
              product_id: chosenProductId,
              location_code: "",
              scrap_qty: scrapQty,
            },
          ]
        }

        const allocations = getInputAllocations(chosenProductId).filter(
          (allocation, index, allAllocations) =>
            allocation.locationCode.trim() &&
            allAllocations.findIndex(
              (candidate) =>
                candidate.locationCode.trim() ===
                  allocation.locationCode.trim() &&
                (candidate.warehouseLotId ?? "") ===
                  (allocation.warehouseLotId ?? ""),
            ) === index,
        )

        return allocations.map((allocation, index) => ({
          product_id: chosenProductId,
          location_code: allocation.locationCode.trim(),
          ...(allocation.warehouseLotId
            ? { warehouse_lot_id: allocation.warehouseLotId }
            : {}),
          scrap_qty: index === 0 ? scrapQty : 0,
        }))
      }),
    [getInputAllocations, productMap, technology, selectedProductPerSlot],
  )

  const isTechnologyLocked = useMemo(
    () => meldunkiTotal > 0 || (order?.produced_qty ?? 0) > 0,
    [meldunkiTotal, order?.produced_qty],
  )

  const formatMeldunekError = (err: any) => {
    const raw =
      err?.body?.error ?? err?.message ?? (typeof err === "string" ? err : "")
    if (!raw) return "Spróbuj ponownie"

    // Regular expression for UUID v4
    const uuidRegex =
      /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi

    return raw.replace(uuidRegex, (id: string) => {
      const lowerId = id.toLowerCase()
      const product = productMap.get(lowerId)
      if (product) {
        return `"${product.name}"`
      }
      return id
    })
  }

  const handlePreviewPdf = async () => {
    try {
      await previewDocumentPdf(
        `/v1/production-orders/${id}/pdf`,
        `${order?.number ?? id}.pdf`,
      )
    } catch (err: any) {
      toast({
        title: "Nie udało się otworzyć PDF",
        description: err?.message ?? "Spróbuj ponownie",
        status: "error",
      })
    }
  }

  const handleDownloadPdf = async () => {
    try {
      await previewDocumentPdf(
        `/v1/production-orders/${id}/pdf`,
        `${order?.number ?? id}.pdf`,
      )
    } catch (err: any) {
      toast({
        title: "Nie udało się pobrać PDF",
        description: err?.message ?? "Spróbuj ponownie",
        status: "error",
      })
    }
  }

  const selectedMeldunekIds = useMemo(
    () => Object.keys(selectedMeldunkiMap),
    [selectedMeldunkiMap],
  )

  const selectedMeldunki = useMemo(
    () => Object.values(selectedMeldunkiMap),
    [selectedMeldunkiMap],
  )

  const selectedCurrentPageCount = useMemo(() => {
    const selectedIds = new Set(Object.keys(selectedMeldunkiMap))
    return meldunki.filter((m) => selectedIds.has(m.id)).length
  }, [meldunki, selectedMeldunkiMap])

  const allMeldunkiSelected =
    meldunki.length > 0 && selectedCurrentPageCount === meldunki.length
  const someMeldunkiSelected =
    selectedCurrentPageCount > 0 && !allMeldunkiSelected

  const toggleMeldunek = (meldunek: MeldunekResponseDTO) => {
    setSelectedMeldunkiMap((prev) => {
      if (prev[meldunek.id]) {
        const next = { ...prev }
        delete next[meldunek.id]
        return next
      }
      return {
        ...prev,
        [meldunek.id]: meldunek,
      }
    })
  }

  const toggleAllMeldunki = () => {
    if (meldunki.length === 0) return
    setSelectedMeldunkiMap((prev) => {
      if (selectedCurrentPageCount === meldunki.length) {
        const next = { ...prev }
        meldunki.forEach((m) => {
          delete next[m.id]
        })
        return next
      }
      const next = { ...prev }
      meldunki.forEach((m) => {
        next[m.id] = m
      })
      return next
    })
  }

  const printMeldunkiLabels = () => {
    if (!order || selectedMeldunki.length === 0) {
      toast({
        title: "Zaznacz meldunki do druku",
        status: "info",
        duration: 2000,
      })
      return
    }

    const labels = selectedMeldunki
      .map((m) => {
        const outputProduct = productMap.get(m.output_product_id)
        return generateLabelHtml(
          order,
          m,
          outputProduct,
          technology,
          activeProduction,
        )
      })
      .join("")

    const html = `<!doctype html>
      <html lang="pl">
        <head>
          <meta charset="utf-8" />
          <title>Etykiety meldunków</title>
          <style>
            ${getLabelStyles()}
          </style>
        </head>
        <body>
          <div class="no-print"><button onclick="window.print()">Drukuj</button></div>
          ${labels}
          <script>window.onload = function(){ window.focus(); };</script>
        </body>
      </html>`

    const w = window.open("", "_blank")
    if (w) {
      w.document.open()
      w.document.write(html)
      w.document.close()
    }
  }

  const printSimpleMeldunkiLabels = () => {
    if (!order || selectedMeldunki.length === 0) {
      toast({
        title: "Zaznacz meldunki do druku",
        status: "info",
        duration: 2000,
      })
      return
    }

    const labels = selectedMeldunki
      .map((m) => {
        const outputProduct = productMap.get(m.output_product_id)
        return generateSimpleMeldunekLabelHtml(m, outputProduct)
      })
      .join("")

    const html = `<!doctype html>
      <html lang="pl">
        <head>
          <meta charset="utf-8" />
          <title>Etykiety meldunków (uproszczone)</title>
          <style>
            ${getSimpleLabelStyles()}
          </style>
        </head>
        <body>
          <div class="no-print"><button onclick="window.print()">Drukuj</button></div>
          ${labels}
          <script>window.onload = function(){ window.focus(); };</script>
        </body>
      </html>`

    const w = window.open("", "_blank")
    if (w) {
      w.document.open()
      w.document.write(html)
      w.document.close()
    }
  }

  const { mutate: updateMeldunek, isPending: isUpdatingMeldunek } = useMutation(
    {
      mutationKey: ["production-orders", "meldunek-update", id],
      mutationFn: (payload: { meldunekId: string; data: MeldunekCreateDTO }) =>
        ProductionOrderService.updateMeldunek(
          id,
          payload.meldunekId,
          payload.data,
        ),
      onSuccess: async () => {
        toast({ title: "Meldunek zaktualizowany", status: "success" })
        await Promise.all([
          qc.invalidateQueries({
            queryKey: ["production-orders", "detail", id],
          }),
          qc.invalidateQueries({
            queryKey: ["production-orders", "meldunki", id],
          }),
          qc.invalidateQueries({ queryKey: ["production-orders", "list"] }),
        ])
        setProducedQty("")
        setNote("")
        setScrapMap({})
        setInputAllocationsMap({})
        setOutputLocationCode("")
        setEditMeldunek(null)
        onClose()
      },
      onError: (err: any) => {
        toast({
          title: "Błąd aktualizacji meldunku",
          description: formatMeldunekError(err),
          status: "error",
        })
      },
    },
  )

  const { mutate: updateOrder, isPending: isUpdatingStatus } = useMutation({
    mutationKey: ["production-orders", "update", id],
    mutationFn: (payload: { status?: string; target_qty?: number }) =>
      ProductionOrderService.updateProductionOrderByID(id, payload),
    onSuccess: async () => {
      toast({ title: "Status zaktualizowany", status: "success" })
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["production-orders", "detail", id] }),
        qc.invalidateQueries({ queryKey: ["production-orders", "list"] }),
      ])
      setEditMode(false)
    },
    onError: (err: any) => {
      toast({
        title: "Błąd zmiany statusu",
        description: err?.message ?? "Spróbuj ponownie",
        status: "error",
      })
    },
  })

  const [meldunekToDelete, setMeldunekToDelete] =
    useState<MeldunekResponseDTO | null>(null)
  const { mutate: deleteMeldunek, isPending: isDeleting } = useMutation({
    mutationKey: ["production-orders", "meldunek-delete", id],
    mutationFn: (meldunekId: string) =>
      ProductionOrderService.deleteMeldunek(id, meldunekId),
    onSuccess: async () => {
      toast({ title: "Meldunek usunięty", status: "success" })
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["production-orders", "detail", id] }),
        qc.invalidateQueries({
          queryKey: ["production-orders", "meldunki", id],
        }),
        qc.invalidateQueries({ queryKey: ["production-orders", "list"] }),
      ])
      // If we deleted the last item on the current page and it's not page 1,
      // step back so we don't land on an empty page.
      if (meldunki.length === 1 && meldunkiPage > 1) {
        setMeldunkiPage((p) => p - 1)
      }
      setSelectedMeldunkiMap((prev) => {
        if (!meldunekToDelete || !prev[meldunekToDelete.id]) return prev
        const next = { ...prev }
        delete next[meldunekToDelete.id]
        return next
      })
      setMeldunekToDelete(null)
      deleteDialog.onClose()
    },
    onError: (err: any) => {
      toast({
        title: "Błąd usuwania meldunku",
        description: formatMeldunekError(err),
        status: "error",
      })
    },
  })

  useEffect(() => {
    if (order?.status) {
      setStatusDraft(order.status)
    }
  }, [order?.status])

  useEffect(() => {
    if (order?.target_qty !== undefined && order?.target_qty !== null) {
      setTargetQtyDraft(`${order.target_qty}`)
    }
  }, [order?.target_qty])

  useEffect(() => {
    if (order?.technology_id) {
      setTechnologyDraft(order.technology_id)
    }
  }, [order?.technology_id])

  useEffect(() => {
    if (order?.start_date) {
      setStartDateDraft(toDateInputValue(order.start_date))
    } else {
      setStartDateDraft("")
    }
    if (order?.planned_end_date) {
      setPlannedEndDateDraft(toDateInputValue(order.planned_end_date))
    } else {
      setPlannedEndDateDraft("")
    }
  }, [order?.start_date, order?.planned_end_date, toDateInputValue])

  useEffect(() => {
    if (meldunki.length === 0) return
    setSelectedMeldunkiMap((prev) => {
      let changed = false
      const next = { ...prev }
      meldunki.forEach((m) => {
        if (next[m.id]) {
          next[m.id] = m
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [meldunki])

  useEffect(() => {
    // Only correct the page when we have fresh (non-stale) data.
    // While keepPreviousData is serving the old page, meldunkiTotalPages
    // temporarily reflects the stale total — we must not reset the page then.
    if (!isMeldunkiPlaceholder && meldunkiPage > meldunkiTotalPages) {
      setMeldunkiPage(meldunkiTotalPages)
    }
  }, [isMeldunkiPlaceholder, meldunkiPage, meldunkiTotalPages])

  const handleSaveOrderChanges = () => {
    const payload: {
      status?: string
      target_qty?: number
      technology_id?: string
      start_date?: string
      planned_end_date?: string
    } = {}
    const nextTarget = Number.parseFloat(targetQtyDraft)
    if (
      Number.isFinite(nextTarget) &&
      nextTarget > 0 &&
      nextTarget !== order?.target_qty
    ) {
      payload.target_qty = nextTarget
    }
    if (
      !isTechnologyLocked &&
      technologyDraft &&
      technologyDraft !== order?.technology_id
    ) {
      payload.technology_id = technologyDraft
    }
    if (statusDraft && statusDraft !== order?.status) {
      payload.status = statusDraft
    }
    const currentStart = toDateInputValue(order?.start_date)
    const currentEnd = toDateInputValue(order?.planned_end_date)
    if (startDateDraft !== currentStart) {
      payload.start_date = startDateDraft || ""
    }
    if (plannedEndDateDraft !== currentEnd) {
      payload.planned_end_date = plannedEndDateDraft || ""
    }
    if (Object.keys(payload).length === 0) {
      toast({ title: "Brak zmian", status: "info" })
      setEditMode(false)
      return
    }
    updateOrder(payload)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "blue"
      case "closed":
        return "green"
      case "cancelled":
        return "red"
      case "draft":
        return "gray"
      default:
        return "blue"
    }
  }

  if (isPending) {
    return (
      <Flex justify="center" align="center" w="full" h="60vh">
        <Spinner size="xl" color="main.accent" />
      </Flex>
    )
  }

  if (isError || !order) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        Nie udało się pobrać zlecenia: {(error as any)?.message ?? "Błąd"}
      </Alert>
    )
  }

  return (
    <Box
      maxW="1400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      h={{ base: "auto", xl: "calc(99vh - 100px)" }}
      overflow={{ base: "visible", xl: "hidden" }}
    >
      {/* Header Section */}
      <Card mb={2} variant="outline" boxShadow="sm">
        <CardBody>
          <Grid
            templateColumns={{ base: "1fr", xl: "minmax(0, 1fr) 280px" }}
            templateRows={{ base: "auto", xl: "auto auto" }}
            gap={4}
            alignItems="start"
          >
            <Stack
              spacing={3}
              gridColumn={{ base: "1", xl: "1" }}
              gridRow={{ base: "1", xl: "1" }}
            >
              <Flex
                direction={{ base: "column", md: "row" }}
                align={{ base: "flex-start", md: "center" }}
                gap={{ base: 2, md: 3 }}
                wrap="wrap"
              >
                <Badge
                  colorScheme={getStatusColor(order.status)}
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  {order.status.toUpperCase()}
                </Badge>
                <Badge variant="outline" colorScheme="blue" px={2} py={1}>
                  {technology?.code ?? order.technology_id}
                </Badge>
                <Heading size="md" color={headingColor} lineHeight="1.1">
                  Zlecenie {order.number}
                </Heading>
              </Flex>

              <Text color={mutedTextColor} fontSize="sm">
                {technology?.name || "Brak przypisanej technologii"}
              </Text>
            </Stack>

            <Stack spacing={4} minW={{ base: "auto", xl: "280px" }}>
              <Flex
                direction="row"
                wrap="wrap"
                gap={2}
                justify={{ base: "flex-start", xl: "flex-end" }}
                align="flex-start"
              >
                <Tooltip label="Dodaj meldunek" hasArrow>
                  <IconButton
                    size="sm"
                    aria-label="Dodaj meldunek"
                    icon={<FiPlus />}
                    colorScheme="green"
                    onClick={createMeldunekModal.onOpen}
                  />
                </Tooltip>

                <Tooltip label={editMode ? "Anuluj edycję" : "Edytuj"} hasArrow>
                  <IconButton
                    size="sm"
                    aria-label={editMode ? "Anuluj edycję" : "Edytuj"}
                    icon={editMode ? <FiX /> : <FiEdit2 />}
                    variant={editMode ? "ghost" : "outline"}
                    colorScheme={editMode ? "red" : "blue"}
                    onClick={() => {
                      if (editMode) {
                        setEditMode(false)
                      } else {
                        setEditMode(true)
                        setStatusDraft(order.status)
                        setTargetQtyDraft(`${order.target_qty}`)
                        setTechnologyDraft(order.technology_id)
                        setStartDateDraft(toDateInputValue(order.start_date))
                        setPlannedEndDateDraft(
                          toDateInputValue(order.planned_end_date),
                        )
                      }
                    }}
                  />
                </Tooltip>

                <Tooltip label="Materiały według technologii" hasArrow>
                  <IconButton
                    size="sm"
                    aria-label="Materiały według technologii"
                    icon={<FiLayers />}
                    variant="outline"
                    colorScheme="blue"
                    onClick={materialsModal.onOpen}
                  />
                </Tooltip>

                <Tooltip label="Derewo technologii produktu" hasArrow>
                  <IconButton
                    size="sm"
                    aria-label="Derewo technologii produktu"
                    icon={<FiShare2 />}
                    colorScheme="purple"
                    variant="outline"
                    onClick={() => {
                      const techId = order.technology_id
                      const prodId = order.output_product_id
                      const query = techId
                        ? `?root_technology_id=${techId}`
                        : prodId
                          ? `?root_product_id=${prodId}`
                          : ""
                      router.history.push(`/WMS/technologies/tree${query}`)
                    }}
                  />
                </Tooltip>

                <Tooltip label="Mapa / Derewo produktu" hasArrow>
                  <IconButton
                    size="sm"
                    aria-label="Mapa продукції produktu"
                    icon={<FiGitBranch />}
                    colorScheme="teal"
                    variant="outline"
                    onClick={() => {
                      const prodId = order.output_product_id
                      const query = prodId ? `?product_id=${prodId}` : ""
                      router.history.push(
                        `/WMS/production-orders/chain${query}`,
                      )
                    }}
                  />
                </Tooltip>

                <PrintLanguageSelect
                  onPrintPL={handlePreviewPdf}
                  onPrintEN={handleDownloadPdf}
                  size="sm"
                />
              </Flex>
            </Stack>

            <SimpleGrid
              columns={{ base: 2, md: 4, xl: 7 }}
              spacing={3}
              gridColumn={{ base: "1", xl: "1" }}
              gridRow={{ base: "2", xl: "2" }}
            >
              <Box p={3} borderWidth="1px" borderRadius="md" bg={statBlueBg}>
                <Text
                  fontSize="xs"
                  color={mutedTextColor}
                  textTransform="uppercase"
                >
                  Cel
                </Text>
                <Text fontWeight="bold">{formatNumber(order.target_qty)}</Text>
              </Box>
              <Box p={3} borderWidth="1px" borderRadius="md" bg={statGreenBg}>
                <Text
                  fontSize="xs"
                  color={mutedTextColor}
                  textTransform="uppercase"
                >
                  Wykonano
                </Text>
                <Text fontWeight="bold">
                  {formatNumber(order.produced_qty)}
                </Text>
              </Box>
              <Box
                p={3}
                borderWidth="1px"
                borderRadius="md"
                bg={remaining > 0 ? statOrangeBg : statGreenBg}
              >
                <Text
                  fontSize="xs"
                  color={mutedTextColor}
                  textTransform="uppercase"
                >
                  Pozostało
                </Text>
                <Text fontWeight="bold">{formatNumber(remaining)}</Text>
              </Box>
              <Box p={3} borderWidth="1px" borderRadius="md">
                <Text
                  fontSize="xs"
                  color={mutedTextColor}
                  textTransform="uppercase"
                >
                  Start
                </Text>
                <Text fontWeight="bold">{fmtDate(order.start_date)}</Text>
              </Box>
              <Box p={3} borderWidth="1px" borderRadius="md">
                <Text
                  fontSize="xs"
                  color={mutedTextColor}
                  textTransform="uppercase"
                >
                  Koniec
                </Text>
                <Text fontWeight="bold">{fmtDate(order.planned_end_date)}</Text>
              </Box>
              <Box p={3} borderWidth="1px" borderRadius="md">
                <Text
                  fontSize="xs"
                  color={mutedTextColor}
                  textTransform="uppercase"
                >
                  Materiałów
                </Text>
                <Text fontWeight="bold">
                  {warehouseMap.get(order.input_warehouse_id)?.code ??
                    order.input_warehouse_id}
                </Text>
              </Box>
              <Box p={3} borderWidth="1px" borderRadius="md">
                <Text
                  fontSize="xs"
                  color={mutedTextColor}
                  textTransform="uppercase"
                >
                  Produkcji
                </Text>
                <Text fontWeight="bold">
                  {warehouseMap.get(order.output_warehouse_id)?.code ??
                    order.output_warehouse_id}
                </Text>
              </Box>
            </SimpleGrid>

            <Box
              borderWidth="1px"
              borderRadius="md"
              p={3}
              gridColumn={{ base: "1", xl: "2" }}
              gridRow={{ base: "3", xl: "2" }}
              alignSelf="stretch"
            >
              <SimpleGrid columns={2} spacing={3}>
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    Data
                  </Text>
                  <Text fontSize="sm">
                    {order.updated_at
                      ? new Date(order.updated_at).toLocaleDateString()
                      : "—"}
                  </Text>
                  <Text fontSize="xs" color={mutedTextColor}>
                    {order.updated_at
                      ? new Date(order.updated_at).toLocaleTimeString()
                      : "—"}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    Kto
                  </Text>
                  <Text fontSize="sm" color="blue.500" fontWeight="medium">
                    {order.updated_by?.acronym ?? "—"}
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>
          </Grid>
        </CardBody>
      </Card>

      {/* Edit Mode Panel */}
      {editMode && (
        <Card
          mb={6}
          border="2px"
          borderColor={editPanelBorder}
          boxShadow="md"
          bg={editPanelBg}
        >
          <CardBody>
            <Heading
              size="sm"
              mb={4}
              display="flex"
              alignItems="center"
              color={editHeadingColor}
            >
              <Icon as={FiEdit2} mr={2} /> Edycja zlecenia
            </Heading>
            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                xl: "repeat(5, minmax(0, 1fr))",
              }}
              gap={4}
            >
              <GridItem>
                <FormControl>
                  <FormLabel fontWeight="bold">Status zlecenia</FormLabel>
                  <Select
                    bg={formFieldBg}
                    value={statusDraft}
                    onChange={(e) => setStatusDraft(e.target.value)}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="closed">Closed</option>
                    <option value="cancelled">Cancelled</option>
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontWeight="bold">Ilość docelowa (Cel)</FormLabel>
                  <Input
                    bg={formFieldBg}
                    type="number"
                    step="any"
                    value={targetQtyDraft}
                    onChange={(e) => setTargetQtyDraft(e.target.value)}
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontWeight="bold">Technologia</FormLabel>
                  <Select
                    bg={formFieldBg}
                    value={technologyDraft}
                    isDisabled={isTechnologyLocked}
                    onChange={(e) => setTechnologyDraft(e.target.value)}
                  >
                    {(technologies ?? []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.code} — {t.name}
                      </option>
                    ))}
                  </Select>
                  {isTechnologyLocked ? (
                    <Text mt={1} fontSize="xs" color={mutedTextColor}>
                      Technologia jest zablokowana po rozpoczęciu produkcji.
                    </Text>
                  ) : null}
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontWeight="bold">Data startu</FormLabel>
                  <Input
                    bg={formFieldBg}
                    type="date"
                    value={startDateDraft}
                    onChange={(e) => setStartDateDraft(e.target.value)}
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontWeight="bold">Data zakonczenia</FormLabel>
                  <Input
                    bg={formFieldBg}
                    type="date"
                    value={plannedEndDateDraft}
                    onChange={(e) => setPlannedEndDateDraft(e.target.value)}
                  />
                </FormControl>
              </GridItem>
            </Grid>
            <Flex mt={5} justify="flex-end" gap={3}>
              <Button variant="ghost" onClick={() => setEditMode(false)}>
                Anuluj
              </Button>
              <Button
                colorScheme="blue"
                leftIcon={<FiCheck />}
                onClick={handleSaveOrderChanges}
                isLoading={isUpdatingStatus}
              >
                Zapisz zmiany
              </Button>
            </Flex>
          </CardBody>
        </Card>
      )}

      {/* Materials modal */}
      <Modal
        isOpen={materialsModal.isOpen}
        onClose={materialsModal.onClose}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader display="flex" alignItems="center" gap={2}>
            <Icon as={FiLayers} color="blue.500" />
            Materiały według technologii
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th>Materiał</Th>
                    <Th>SKU</Th>
                    <Th>Jedn.</Th>
                    <Th isNumeric>Ilość w pak.</Th>
                    <Th isNumeric>Ilość na Zlecenie</Th>
                    <Th isNumeric>Braki %</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(technology?.inputs ?? []).flatMap((inp) => {
                    const prod = productMap.get(inp.product_id)
                    const batchSize = order.batch_size || 1
                    const totalNetto = inp.qty * (order.target_qty / batchSize)
                    const totalBrutto =
                      totalNetto * (1 + (inp.loss_percent ?? 0) / 100)
                    const hasSubstitutes = (inp.substitutes ?? []).length > 0

                    const primaryRow = (
                      <Tr key={inp.product_id} _hover={{ bg: hoverBg }}>
                        <Td fontWeight="medium">
                          <Flex align="center" gap={2}>
                            {prod ? prod.name : inp.product_id}
                            {hasSubstitutes && (
                              <Badge
                                fontSize="9px"
                                colorScheme="purple"
                                variant="subtle"
                                flexShrink={0}
                              >
                                +{inp.substitutes!.length} zam.
                              </Badge>
                            )}
                          </Flex>
                        </Td>
                        <Td fontFamily="mono" fontSize="xs">
                          {prod ? prod.sku : "—"}
                        </Td>
                        <Td>{prod ? prod.unit : "—"}</Td>
                        <Td isNumeric>{formatNumber(inp.qty)}</Td>
                        <Td isNumeric fontWeight="bold" color="blue.600">
                          {formatNumber(totalBrutto)}
                        </Td>
                        <Td isNumeric>{inp.loss_percent ?? 0}%</Td>
                      </Tr>
                    )

                    const substituteRows = (inp.substitutes ?? []).map(
                      (sub) => {
                        const subTotalNetto =
                          sub.qty * (order.target_qty / batchSize)
                        const subTotalBrutto =
                          subTotalNetto * (1 + (sub.loss_percent ?? 0) / 100)
                        return (
                          <Tr
                            key={`sub-${sub.substitute_product_id}`}
                            _hover={{ bg: hoverBg }}
                            opacity={0.8}
                          >
                            <Td>
                              <Flex align="center" gap={2} pl={4}>
                                <Box
                                  w="2px"
                                  h="14px"
                                  bg="purple.300"
                                  borderRadius="full"
                                  flexShrink={0}
                                />
                                <Badge
                                  fontSize="9px"
                                  colorScheme="purple"
                                  variant="outline"
                                  flexShrink={0}
                                >
                                  Zamiennik
                                </Badge>
                                <Text fontSize="sm" color={mutedTextColor}>
                                  {sub.product?.name ??
                                    sub.substitute_product_id}
                                </Text>
                                {sub.note && (
                                  <Tooltip label={sub.note} hasArrow>
                                    <Text
                                      fontSize="xs"
                                      color="gray.400"
                                      isTruncated
                                      maxW="120px"
                                    >
                                      {sub.note}
                                    </Text>
                                  </Tooltip>
                                )}
                              </Flex>
                            </Td>
                            <Td
                              fontFamily="mono"
                              fontSize="xs"
                              color={mutedTextColor}
                            >
                              {sub.product?.sku ?? "—"}
                            </Td>
                            <Td color={mutedTextColor}>
                              {sub.product?.unit ?? "—"}
                            </Td>
                            <Td isNumeric color={mutedTextColor}>
                              {formatNumber(sub.qty)}
                            </Td>
                            <Td isNumeric fontWeight="bold" color="purple.500">
                              {formatNumber(subTotalBrutto)}
                            </Td>
                            <Td isNumeric color={mutedTextColor}>
                              {sub.loss_percent ?? 0}%
                            </Td>
                          </Tr>
                        )
                      },
                    )

                    return [primaryRow, ...substituteRows]
                  })}
                  {(!technology?.inputs || technology.inputs.length === 0) && (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={4}>
                        <Text color="gray.500">
                          Brak materiałów w technologii
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Card variant="outline" boxShadow="sm" mb={2} flex="1" minH={0}>
        <CardBody
          p={{ base: 3, md: 4 }}
          display="flex"
          flexDirection="column"
          minH={0}
          h="full"
        >
          <Flex
            direction={{ base: "column", lg: "row" }}
            justify="space-between"
            align={{ base: "stretch", lg: "center" }}
            mb={4}
            gap={3}
          >
            <Box>
              <Heading size="md" display="flex" alignItems="center">
                <Icon as={FiDatabase} mr={2} color="green.500" /> Meldunki
              </Heading>
              <Text color="gray.500" fontSize="sm" mt={1}>
                Zaznacz meldunki do wydruku etykiet i zarządzania dokumentami.
              </Text>
            </Box>
            <HStack
              spacing={2}
              justify={{ base: "flex-end", lg: "initial" }}
              flexWrap="wrap"
            >
              <Badge
                variant="subtle"
                colorScheme="green"
                borderRadius="full"
                px={3}
                py={1}
              >
                Zaznaczone: {selectedMeldunki.length}
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<PiBroom />}
                onClick={() => setSelectedMeldunkiMap({})}
                isDisabled={selectedMeldunki.length === 0}
              >
                Wyczyść zaznaczenie
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={printMeldunkiLabels}
                isDisabled={selectedMeldunki.length === 0}
                leftIcon={<FiPrinter />}
              >
                Drukuj pełne ({selectedMeldunki.length})
              </Button>
              <Button
                size="sm"
                colorScheme="teal"
                variant="outline"
                onClick={printSimpleMeldunkiLabels}
                isDisabled={selectedMeldunki.length === 0}
                leftIcon={<FiPrinter />}
              >
                Drukuj uproszczone ({selectedMeldunki.length})
              </Button>
            </HStack>
          </Flex>

          <TableContainer
            flex="1"
            minH={0}
            borderWidth="1px"
            borderRadius="2xl"
            overflowX="auto"
            overflowY="auto"
          >
            <Table variant="simple" size="sm">
              <Thead bg={tableHeaderBg} position="sticky" top={0} zIndex={2}>
                <Tr>
                  <Th w="40px">
                    <Checkbox
                      isChecked={allMeldunkiSelected}
                      isIndeterminate={someMeldunkiSelected}
                      onChange={toggleAllMeldunki}
                    />
                  </Th>
                  <Th>Nr</Th>
                  <Th>Numer</Th>
                  <Th>Wyjścia</Th>
                  <Th w="140px">Lok. wyrobu</Th>
                  <Th isNumeric>Realiz.</Th>
                  <Th isNumeric>Ilość</Th>
                  <Th isNumeric>Wart.</Th>
                  <Th isNumeric>Cena</Th>
                  <Th>Data</Th>
                  <Th
                    textAlign="right"
                    position="sticky"
                    right={0}
                    zIndex={2}
                    bg={tableHeaderBg}
                    boxShadow="-6px 0 8px -8px rgba(0, 0, 0, 0.25)"
                  >
                    Akcja
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {meldunki.map((m) => (
                  <Tr key={m.id} _hover={{ bg: hoverBg }}>
                    <Td>
                      <Checkbox
                        isChecked={selectedMeldunekIds.includes(m.id)}
                        onChange={() => toggleMeldunek(m)}
                      />
                    </Td>
                    <Td color="gray.500">{m.seq_no ?? "—"}</Td>
                    <Td fontWeight="medium">{m.number}</Td>
                    <Td
                      maxW="360px"
                      whiteSpace="normal"
                      title={formatMeldunekOutputs(m)}
                    >
                      {formatMeldunekOutputs(m)}
                    </Td>
                    <Td
                      maxW="140px"
                      isTruncated
                      title={m.output_location_code || ""}
                    >
                      {m.output_location_code || "—"}
                    </Td>
                    <Td isNumeric fontWeight="bold">
                      {formatNumber(m.run_count)}
                    </Td>
                    <Td isNumeric color="gray.500" fontSize="sm">
                      {formatNumber(m.produced_qty)}
                    </Td>
                    <Td isNumeric fontSize="sm" fontWeight="semibold">
                      {fmtMoney(m.total_cost_netto)}
                    </Td>
                    <Td
                      isNumeric
                      fontSize="sm"
                      color="blue.500"
                      fontWeight="bold"
                    >
                      {fmtMoney(m.unit_cost_netto)}
                    </Td>
                    <Td fontSize="xs" whiteSpace="nowrap">
                      {m.created_at
                        ? new Date(m.created_at).toLocaleString()
                        : "—"}
                    </Td>
                    <Td
                      textAlign="right"
                      position="sticky"
                      right={0}
                      zIndex={1}
                      bg={stickyCellBg}
                      boxShadow="-6px 0 8px -8px rgba(0, 0, 0, 0.25)"
                    >
                      <HStack justify="flex-end" spacing={0.5}>
                        <Tooltip label="Info" hasArrow>
                          <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label="Info"
                            icon={<FiInfo />}
                            onClick={() => setInfoMeldunek(m)}
                          />
                        </Tooltip>
                        <Tooltip label="Karta Partii / Specyfikacja" hasArrow>
                          <IconButton
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            aria-label="Karta Partii / Specyfikacja"
                            icon={<FiFileText />}
                            onClick={() =>
                              router.history.push(
                                `/WMS/production-orders/${
                                  order.id ?? id
                                }?meldunek=${encodeURIComponent(m.id)}`,
                              )
                            }
                          />
                        </Tooltip>
                        <Tooltip label="Edytuj" hasArrow>
                          <IconButton
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            aria-label="Edytuj"
                            icon={<FiEdit2 />}
                            onClick={() => {
                              setEditMeldunek(m)
                              setProducedQty(`${m.run_count}`)
                              setNote(m.note ?? "")
                              const map: Record<string, string> = {}
                              const allocationsByProduct: Record<
                                string,
                                MaterialAllocationDraft[]
                              > = {}
                              const selectedPerSlot: Record<string, string> = {}
                              ;(technology?.inputs ?? []).forEach((inp) => {
                                const itemMatch = (m.items ?? []).find(
                                  (it) =>
                                    it.product_id === inp.product_id ||
                                    (inp.substitutes ?? []).some(
                                      (s) =>
                                        s.substitute_product_id ===
                                        it.product_id,
                                    ),
                                )
                                if (itemMatch) {
                                  selectedPerSlot[inp.id] = itemMatch.product_id
                                }
                              })
                              setSelectedProductPerSlot(selectedPerSlot)
                              ;(m.items ?? []).forEach((it) => {
                                map[it.product_id] = `${
                                  Number(map[it.product_id] ?? "0") +
                                  (it.scrap_qty ?? 0)
                                }`
                                if (it.location_code) {
                                  allocationsByProduct[it.product_id] = [
                                    ...(allocationsByProduct[it.product_id] ??
                                      []),
                                    createAllocationDraft({
                                      id: it.id,
                                      locationCode: it.location_code ?? "",
                                      warehouseLotId:
                                        it.warehouse_lot_id ?? undefined,
                                      lotLabel: it.warehouse_lot_id
                                        ? "Partia produkcyjna"
                                        : undefined,
                                    }),
                                  ]
                                }
                              })
                              setScrapMap(map)
                              setInputAllocationsMap(allocationsByProduct)
                              setOutputLocationCode(
                                m.output_location_code ?? "",
                              )
                              onOpen()
                            }}
                          />
                        </Tooltip>
                        <Tooltip label="Usuń" hasArrow>
                          <IconButton
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            aria-label="Usuń"
                            icon={<FiX />}
                            onClick={() => {
                              setMeldunekToDelete(m)
                              deleteDialog.onOpen()
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
                {meldunki.length === 0 && (
                  <Tr>
                    <Td colSpan={11} textAlign="center" py={4}>
                      <Text color="gray.500">Brak meldunków</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
          {meldunkiTotal > 0 && (
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "stretch", md: "center" }}
              gap={3}
              pt={4}
              flexShrink={0}
            >
              <Text fontSize="sm" color={mutedTextColor}>
                Pokazano {(meldunkiPage - 1) * MELDUNKI_PAGE_SIZE + 1}-
                {(meldunkiPage - 1) * MELDUNKI_PAGE_SIZE + meldunki.length} z{" "}
                {meldunkiTotal}
              </Text>
              <PaginationFooter
                mt={0}
                page={meldunkiPage}
                onChangePage={setMeldunkiPage}
                hasPreviousPage={meldunkiPage > 1}
                hasNextPage={meldunkiPage < meldunkiTotalPages}
              />
            </Flex>
          )}
        </CardBody>
      </Card>

      <ProductionMeldunekCreateModal
        orderId={id}
        isOpen={createMeldunekModal.isOpen}
        onClose={createMeldunekModal.onClose}
        onCreated={async (createdCount) => {
          setMeldunkiPage(
            Math.max(
              1,
              Math.ceil((meldunkiTotal + createdCount) / MELDUNKI_PAGE_SIZE),
            ),
          )
        }}
      />

      <Modal
        isOpen={isOpen && !!editMeldunek}
        onClose={() => {
          setEditMeldunek(null)
          onClose()
        }}
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edycja meldunku</ModalHeader>
          <ModalBody pb={6}>
            <HStack spacing={4} mb={4}>
              <Box flex={1}>
                <Text fontWeight="semibold" mb={1}>
                  Liczba realizacji technologii
                </Text>
                <NumberInput
                  min={0}
                  step={1}
                  value={producedQty}
                  onChange={(valueString) => setProducedQty(valueString)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                {order.target_qty > 0 &&
                  Number.parseFloat(producedQty) > remaining && (
                    <Text color="red.500" fontSize="sm">
                      Ilość realizacji przekracza pozostałą (
                      {formatNumber(remaining)})
                    </Text>
                  )}
              </Box>
              <Box flex={1}>
                <Text fontWeight="semibold" mb={1}>
                  Uwagi
                </Text>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Box>
              <Box flex={1}>
                <Text fontWeight="semibold" mb={1}>
                  Lokalizacja produktów wyjściowych
                </Text>
                <WarehouseLocationSelect
                  warehouseId={order.output_warehouse_id}
                  productId={mainOutput?.product_id}
                  value={outputLocationCode}
                  onChange={setOutputLocationCode}
                  title="Wybierz lokalizację produktów wyjściowych"
                />
              </Box>
            </HStack>

            {plannedOutputs.length > 0 ? (
              <Box mb={4}>
                <Heading size="sm" mb={2}>
                  Wyjścia wg technologii
                </Heading>
                <TableContainer borderWidth="1px" borderRadius="md">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Produkt (SKU • Nazwa)</Th>
                        <Th isNumeric>Ilość</Th>
                        <Th>Rola</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {plannedOutputs.map((output) => {
                        const product = productMap.get(output.product_id)
                        const skuLabel = product?.sku ?? "—"
                        const nameLabel = product?.name ?? output.product_id
                        return (
                          <Tr key={output.product_id}>
                            <Td>
                              <Box>
                                <Tooltip
                                  label={skuLabel}
                                  placement="top-start"
                                  hasArrow
                                  isDisabled={!product?.sku}
                                >
                                  <Text
                                    maxW="150px"
                                    isTruncated
                                    fontSize="xs"
                                    color="gray.500"
                                    fontWeight="semibold"
                                    mb={0.5}
                                  >
                                    {skuLabel}
                                  </Text>
                                </Tooltip>
                                <Tooltip
                                  label={nameLabel}
                                  placement="top-start"
                                  hasArrow
                                  isDisabled={!nameLabel}
                                >
                                  <Text maxW="250px" isTruncated>
                                    {nameLabel}
                                  </Text>
                                </Tooltip>
                              </Box>
                            </Td>
                            <Td isNumeric>
                              {formatNumber(output.qty_produced)}
                            </Td>
                            <Td>
                              {output.is_main ? "Referencyjny" : "Dodatkowy"}
                            </Td>
                          </Tr>
                        )
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            ) : null}

            <Heading size="sm" mb={2} display="flex" alignItems="center">
              <Icon as={FiDatabase} mr={2} /> Materiały wg technologii
            </Heading>
            <Alert
              status="info"
              mb={4}
              py={2}
              borderRadius="md"
              variant="subtle"
            >
              <AlertIcon />
              <Text fontSize="sm">
                Meldunek pozwala rozliczyć materiał z wielu lokalizacji lub
                partii dla tej samej pozycji towarowej.
              </Text>
            </Alert>
            <TableContainer borderWidth="1px" borderRadius="md" mb={4}>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Produkt (SKU • Nazwa)</Th>
                    <Th>Miejsce</Th>
                    <Th>Plan</Th>
                    <Th>Błąd / Brak</Th>
                    <Th>Zużycie</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(technology?.inputs ?? []).map((inp) => {
                    const chosenProductId =
                      selectedProductPerSlot[inp.id] ?? inp.product_id
                    const plannedRow = plannedInputs.find(
                      (p) => p.slot_id === inp.id,
                    )
                    const usedQty = plannedRow?.used_qty ?? 0
                    const product = productMap.get(chosenProductId)
                    const isService = !!product?.is_service
                    const skuLabel = product?.sku ?? "—"
                    const nameLabel = product?.name ?? chosenProductId
                    const hasSubstitutes = (inp.substitutes ?? []).length > 0

                    return (
                      <Tr key={inp.id}>
                        <Td>
                          <Box>
                            {hasSubstitutes ? (
                              <Box mb={1}>
                                <Text
                                  fontSize="xs"
                                  fontWeight="bold"
                                  color="blue.600"
                                  mb={1}
                                >
                                  Wybór materiału (główny / замінник):
                                </Text>
                                <Select
                                  size="sm"
                                  value={chosenProductId}
                                  onChange={(e) => {
                                    const val = e.target.value
                                    setSelectedProductPerSlot((prev) => ({
                                      ...prev,
                                      [inp.id]: val,
                                    }))
                                  }}
                                >
                                  {(() => {
                                    const primProd = productMap.get(
                                      inp.product_id,
                                    )
                                    return (
                                      <option value={inp.product_id}>
                                        Główny:{" "}
                                        {primProd?.sku
                                          ? `${primProd.sku} • `
                                          : ""}
                                        {primProd?.name ?? inp.product_id}{" "}
                                        (norma: {inp.qty})
                                      </option>
                                    )
                                  })()}
                                  {(inp.substitutes ?? []).map((sub) => {
                                    const subProd = productMap.get(
                                      sub.substitute_product_id,
                                    )
                                    return (
                                      <option
                                        key={sub.id}
                                        value={sub.substitute_product_id}
                                      >
                                        Zaмінник:{" "}
                                        {subProd?.sku
                                          ? `${subProd.sku} • `
                                          : ""}
                                        {subProd?.name ??
                                          sub.substitute_product_id}{" "}
                                        (norma: {sub.qty}, loss:{" "}
                                        {sub.loss_percent}%)
                                      </option>
                                    )
                                  })}
                                </Select>
                              </Box>
                            ) : (
                              <>
                                <Tooltip
                                  label={skuLabel}
                                  placement="top-start"
                                  hasArrow
                                  isDisabled={!product?.sku}
                                >
                                  <Text
                                    maxW="150px"
                                    isTruncated
                                    fontSize="xs"
                                    color="gray.500"
                                    fontWeight="semibold"
                                    mb={0.5}
                                  >
                                    {skuLabel}
                                  </Text>
                                </Tooltip>
                                <Tooltip
                                  label={nameLabel}
                                  placement="top-start"
                                  hasArrow
                                  isDisabled={!nameLabel}
                                >
                                  <Text maxW="250px" isTruncated>
                                    {nameLabel}
                                  </Text>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </Td>
                        <Td>
                          {isService ? (
                            <Text color={mutedTextColor}>
                              Nie dotyczy dla usługi
                            </Text>
                          ) : (
                            <Stack spacing={2}>
                              {getInputAllocations(chosenProductId).map(
                                (allocation, allocationIndex, allocations) => (
                                  <Box key={allocation.id}>
                                    <HStack align="start">
                                      <Box flex={1}>
                                        <Input
                                          size="sm"
                                          value={allocation.locationCode}
                                          onChange={(e) =>
                                            updateInputAllocation(
                                              chosenProductId,
                                              allocation.id,
                                              (current) => ({
                                                ...current,
                                                locationCode: e.target.value,
                                                warehouseLotId: undefined,
                                                lotLabel: undefined,
                                              }),
                                            )
                                          }
                                          onKeyDown={(e) => e.preventDefault()}
                                          onClick={() => {
                                            setPickerProductId(chosenProductId)
                                            setPickerAllocationId(allocation.id)
                                            materialLocationPicker.onOpen()
                                          }}
                                          placeholder="np. A-R001-M001"
                                          isReadOnly
                                          readOnly
                                          cursor="pointer"
                                          _focus={{
                                            borderColor: "blue.500",
                                            boxShadow: "outline",
                                          }}
                                        />
                                        {allocation.warehouseLotId ? (
                                          <Text
                                            mt={1}
                                            fontSize="xs"
                                            color={mutedTextColor}
                                          >
                                            Partia:{" "}
                                            {allocation.lotLabel ??
                                              "Partia produkcyjna"}
                                          </Text>
                                        ) : null}
                                      </Box>
                                      <IconButton
                                        size="sm"
                                        variant="ghost"
                                        aria-label="Dodaj źródło"
                                        icon={<FiPlus />}
                                        onClick={() =>
                                          addInputAllocation(chosenProductId)
                                        }
                                      />
                                      <IconButton
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        aria-label="Usuń źródło"
                                        icon={<FiX />}
                                        isDisabled={allocations.length === 1}
                                        onClick={() =>
                                          removeInputAllocation(
                                            chosenProductId,
                                            allocation.id,
                                          )
                                        }
                                      />
                                    </HStack>
                                    {allocationIndex === 0 &&
                                    allocations.length > 1 ? (
                                      <Text
                                        mt={1}
                                        fontSize="xs"
                                        color={mutedTextColor}
                                      >
                                        Materiał może zostać rozliczony z wielu
                                        lokalizacji lub partii.
                                      </Text>
                                    ) : null}
                                  </Box>
                                ),
                              )}
                            </Stack>
                          )}
                        </Td>
                        <Td>{formatNumber(plannedRow?.planned_qty ?? 0)}</Td>
                        <Td>
                          <Input
                            size="sm"
                            type="number"
                            step="any"
                            value={scrapMap[chosenProductId] ?? ""}
                            onChange={(e) =>
                              setScrapMap((prev) => ({
                                ...prev,
                                [chosenProductId]: e.target.value,
                              }))
                            }
                          />
                        </Td>
                        <Td>{formatNumber(usedQty)}</Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            </TableContainer>

            <HStack justify="flex-end">
              <Button variant="ghost" onClick={onClose}>
                Anuluj
              </Button>
              <Button
                colorScheme="green"
                isLoading={isUpdatingMeldunek}
                isDisabled={!canCreateMeldunek}
                onClick={() => {
                  if (!canCreateMeldunek) return
                  const qty = Number.parseFloat(producedQty)
                  const totalScrapMap = Object.fromEntries(
                    (technology?.inputs ?? []).map((inp) => [
                      inp.product_id,
                      Number.parseFloat(scrapMap[inp.product_id] ?? "0") || 0,
                    ]),
                  )
                  const itemsTotal = buildMeldunekItems(totalScrapMap)

                  const buildPayload = (
                    runCount: number,
                    items: {
                      product_id: string
                      location_code: string
                      warehouse_lot_id?: string
                      scrap_qty: number
                    }[],
                  ) =>
                    ({
                      run_count: runCount,
                      is_partial: runCount < 1 ? true : undefined,
                      output_location_code: outputLocationCode.trim(),
                      note: note.trim() || undefined,
                      items,
                    }) satisfies MeldunekCreateDTO

                  if (!editMeldunek) return
                  updateMeldunek({
                    meldunekId: editMeldunek.id,
                    data: buildPayload(qty, itemsTotal),
                  })
                }}
              >
                Zapisz
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Usunąć meldunek?</ModalHeader>
          <ModalBody pb={6}>
            <Text>
              Ta operacja cofnie zużycie materiałów i odejmie produkt z
              magazynu.
            </Text>
            <HStack justify="flex-end" mt={4}>
              <Button variant="ghost" onClick={deleteDialog.onClose}>
                Anuluj
              </Button>
              <Button
                colorScheme="red"
                isLoading={isDeleting}
                onClick={() =>
                  meldunekToDelete && deleteMeldunek(meldunekToDelete.id)
                }
              >
                Usuń
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={techChangeDialog.isOpen}
        leastDestructiveRef={techChangeCancelRef}
        onClose={() => {
          setPendingOrderUpdate(null)
          techChangeDialog.onClose()
        }}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Zmienić technologię?
            </AlertDialogHeader>
            <AlertDialogBody>
              Zlecenie ma już meldunki. Zmiana technologii wpłynie na dalsze
              meldunki. Czy na pewno kontynuować?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                onClick={() => {
                  setPendingOrderUpdate(null)
                  techChangeDialog.onClose()
                }}
                variant="outline"
                ref={techChangeCancelRef}
              >
                Anuluj
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  if (pendingOrderUpdate) {
                    updateOrder(pendingOrderUpdate)
                  }
                  setPendingOrderUpdate(null)
                  techChangeDialog.onClose()
                }}
                ml={3}
              >
                Zmień
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <UnifiedProductPickerModal
        isOpen={materialLocationPicker.isOpen}
        onClose={() => {
          setPickerProductId(null)
          setPickerAllocationId(null)
          materialLocationPicker.onClose()
        }}
        warehouseId={order.input_warehouse_id}
        title="Wybierz lokalizację матеріалу (мультивибір)"
        productIdFilter={pickerProductId ?? undefined}
        multiSelect
        disabledLotIds={
          pickerProductId
            ? getInputAllocations(pickerProductId)
                .filter((allocation) => allocation.id !== pickerAllocationId)
                .map((allocation) => allocation.warehouseLotId)
                .filter((lotId): lotId is string => !!lotId)
            : []
        }
        onChoose={(productId, locationCode, lotId, lotNo) => {
          if (
            !pickerProductId ||
            pickerProductId !== productId ||
            !pickerAllocationId
          ) {
            return
          }
          updateInputAllocation(
            productId,
            pickerAllocationId,
            (allocation) => ({
              ...allocation,
              locationCode: locationCode ?? "",
              warehouseLotId: lotId ?? undefined,
              lotLabel: lotNo ?? undefined,
            }),
          )
          setPickerProductId(null)
          setPickerAllocationId(null)
          materialLocationPicker.onClose()
        }}
        onChooseMany={(selections) => {
          if (!pickerProductId || selections.length === 0) return
          setInputAllocationsMap((prev) => {
            const currentAllocations = prev[pickerProductId] ?? [
              createAllocationDraft(),
            ]
            let updated = [...currentAllocations]

            selections.forEach((sel, index) => {
              const newLocationCode = sel.locationCode ?? ""
              const newLotId = sel.lotId ?? undefined
              const newLotLabel = sel.lotNo ?? undefined

              // If targeting a specific allocation row and it's the first selection, update it
              if (
                index === 0 &&
                pickerAllocationId &&
                updated.some((a) => a.id === pickerAllocationId)
              ) {
                updated = updated.map((a) =>
                  a.id === pickerAllocationId
                    ? {
                        ...a,
                        locationCode: newLocationCode,
                        warehouseLotId: newLotId,
                        lotLabel: newLotLabel,
                      }
                    : a,
                )
              } else {
                // Check if an empty unconfigured allocation row exists to overwrite
                const emptyIdx = updated.findIndex(
                  (a) => !a.locationCode && !a.warehouseLotId,
                )
                if (emptyIdx !== -1) {
                  updated[emptyIdx] = {
                    ...updated[emptyIdx],
                    locationCode: newLocationCode,
                    warehouseLotId: newLotId,
                    lotLabel: newLotLabel,
                  }
                } else {
                  // Append new allocation draft
                  updated.push(
                    createAllocationDraft({
                      locationCode: newLocationCode,
                      warehouseLotId: newLotId,
                      lotLabel: newLotLabel,
                    }),
                  )
                }
              }
            })

            return {
              ...prev,
              [pickerProductId]: updated,
            }
          })
          setPickerProductId(null)
          setPickerAllocationId(null)
          materialLocationPicker.onClose()
        }}
      />

      <Modal
        isOpen={!!infoMeldunek}
        onClose={() => setInfoMeldunek(null)}
        size="4xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Informacje o meldunku: {infoMeldunek?.number}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <HStack
                justify="space-between"
                bg={infoMeldunekBg}
                p={3}
                borderRadius="md"
                border="1px"
                borderColor={infoMeldunekBorder}
              >
                <Box>
                  <Text
                    fontSize="xs"
                    color="blue.500"
                    fontWeight="bold"
                    textTransform="uppercase"
                  >
                    Numer meldunku
                  </Text>
                  <Text fontSize="lg" fontWeight="bold">
                    {infoMeldunek?.number || "—"}
                  </Text>
                </Box>
                <Box textAlign="right">
                  <Text
                    fontSize="xs"
                    color="blue.500"
                    fontWeight="bold"
                    textTransform="uppercase"
                  >
                    Realizacje / Ilość
                  </Text>
                  <Text fontSize="lg" fontWeight="bold">
                    {formatNumber(infoMeldunek?.run_count ?? 0)} realiz. ={" "}
                    {infoMeldunek?.outputs && infoMeldunek.outputs.length > 0
                      ? infoMeldunek.outputs
                          .map((out) =>
                            `${formatNumber(out.qty_produced)} ${
                              productMap.get(out.product_id)?.unit || ""
                            }`.trim(),
                          )
                          .join(" + ")
                      : `${formatNumber(infoMeldunek?.produced_qty ?? 0)} ${
                          productMap.get(infoMeldunek?.output_product_id ?? "")
                            ?.unit || ""
                        }`.trim()}
                  </Text>
                </Box>
              </HStack>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box
                  border="1px"
                  borderColor={infoMeldunekBorder}
                  borderRadius="md"
                  p={4}
                >
                  <Text
                    fontWeight="bold"
                    color={mutedTextColor}
                    fontSize="xs"
                    textTransform="uppercase"
                    mb={2}
                  >
                    Produkt Końcowy
                  </Text>
                  <Text fontWeight="semibold">
                    {productMap.get(infoMeldunek?.output_product_id ?? "")
                      ?.name || infoMeldunek?.output_product_id}
                  </Text>
                  <Text fontSize="sm" color={mutedTextColor}>
                    {productMap.get(infoMeldunek?.output_product_id ?? "")
                      ?.sku || "—"}
                  </Text>
                  <Text fontSize="sm" mt={2}>
                    Lokalizacja partii:{" "}
                    <Text as="span" fontWeight="medium">
                      {infoMeldunek?.output_location_code || "—"}
                    </Text>
                  </Text>
                  <Text fontSize="sm">
                    Metoda kalkulacji:{" "}
                    <Text as="span" fontWeight="medium">
                      {infoMeldunek?.costing_method || "—"}
                    </Text>
                  </Text>
                </Box>

                <Box
                  border="1px"
                  borderColor={infoMeldunekBorder}
                  borderRadius="md"
                  p={4}
                >
                  <Text
                    fontWeight="bold"
                    color={mutedTextColor}
                    fontSize="xs"
                    textTransform="uppercase"
                    mb={2}
                  >
                    Cena Produkcji
                  </Text>
                  <Stack spacing={1} fontSize="sm">
                    <Text>
                      Materiały netto:{" "}
                      <Text as="span" fontWeight="semibold">
                        {fmtMoney(infoMeldunek?.material_cost_netto)}
                      </Text>
                    </Text>
                    <Text>
                      Usługi netto:{" "}
                      <Text as="span" fontWeight="semibold">
                        {fmtMoney(infoMeldunek?.service_cost_netto)}
                      </Text>
                    </Text>
                    <Text fontWeight="bold" fontSize="md" pt={1}>
                      Koszt całkowity netto:{" "}
                      {fmtMoney(infoMeldunek?.total_cost_netto)}
                    </Text>
                    <Text fontWeight="bold" color="blue.500">
                      Koszt jednostkowy produktu:{" "}
                      {fmtMoney(infoMeldunek?.unit_cost_netto)}
                    </Text>
                  </Stack>
                </Box>
              </SimpleGrid>

              <Box>
                <Text
                  fontWeight="bold"
                  color={mutedTextColor}
                  fontSize="xs"
                  textTransform="uppercase"
                  mb={2}
                >
                  Wyjścia
                </Text>
                <TableContainer borderWidth="1px" borderRadius="md">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Produkt</Th>
                        <Th>Partia</Th>
                        <Th>Lokacja</Th>
                        <Th isNumeric>Ilość</Th>
                        <Th isNumeric>Pozostało</Th>
                        <Th isNumeric>Koszt jedn.</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(
                        infoMeldunek?.outputs ?? [
                          {
                            product_id: infoMeldunek?.output_product_id ?? "",
                            lot_no: infoMeldunek?.number ?? "",
                            output_location_code:
                              infoMeldunek?.output_location_code ?? "",
                            qty_produced: infoMeldunek?.produced_qty ?? 0,
                            qty_remaining: infoMeldunek?.produced_qty ?? 0,
                            unit_cost_netto: infoMeldunek?.unit_cost_netto ?? 0,
                          },
                        ]
                      ).map((output) => {
                        const product = productMap.get(output.product_id)
                        return (
                          <Tr key={`${output.product_id}-${output.lot_no}`}>
                            <Td>
                              <Text fontWeight="semibold">
                                {product?.name ?? output.product_id}
                              </Text>
                              <Text fontSize="xs" color={mutedTextColor}>
                                {product?.sku ?? "—"}
                              </Text>
                            </Td>
                            <Td>{output.lot_no || "—"}</Td>
                            <Td>{output.output_location_code || "—"}</Td>
                            <Td isNumeric>
                              {formatNumber(output.qty_produced)}
                            </Td>
                            <Td isNumeric>
                              {formatNumber(output.qty_remaining)}
                            </Td>
                            <Td isNumeric>
                              {fmtMoney(output.unit_cost_netto)}
                            </Td>
                          </Tr>
                        )
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Text
                  fontWeight="bold"
                  color={mutedTextColor}
                  fontSize="xs"
                  textTransform="uppercase"
                >
                  Materiały i Pakunki
                </Text>
                <Table variant="simple" size="sm" mt={2}>
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th>Materiał</Th>
                      <Th isNumeric>Ilość</Th>
                      <Th isNumeric>Cena jedn. netto</Th>
                      <Th isNumeric>Wartość netto</Th>
                      <Th>Źródło kosztu</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(infoMeldunek?.cost_components ?? [])
                      .filter((component) => component.qty_actual > 0)
                      .map((it) => {
                        const prod = productMap.get(it.product_id)
                        return (
                          <Tr key={it.id} _hover={{ bg: hoverBg }}>
                            <Td fontSize="sm">
                              <Text fontWeight="medium">
                                {prod?.name || it.product_id}
                              </Text>
                              <Text fontSize="xs" color={mutedTextColor}>
                                {prod?.sku || "—"}
                              </Text>
                              <Text fontSize="xs" color={mutedTextColor}>
                                {it.component_type === "service"
                                  ? "Usługa technologiczna"
                                  : "Materiał / surowiec"}
                              </Text>
                            </Td>
                            <Td isNumeric fontSize="sm" fontWeight="bold">
                              {formatNumber(it.qty_actual)} {prod?.unit}
                            </Td>
                            <Td isNumeric fontSize="sm">
                              {fmtMoney(it.unit_cost_netto)}
                            </Td>
                            <Td isNumeric fontSize="sm" fontWeight="semibold">
                              {fmtMoney(it.total_cost_netto)}
                            </Td>
                            <Td fontSize="xs" color={mutedTextColor}>
                              <Text>{it.source_document_no || "—"}</Text>
                              <Text>{it.source_type || "—"}</Text>
                            </Td>
                          </Tr>
                        )
                      })}
                  </Tbody>
                </Table>
              </Box>

              <Box>
                <Text
                  fontWeight="bold"
                  color={mutedTextColor}
                  fontSize="xs"
                  textTransform="uppercase"
                >
                  Podsumowanie Kalkulacji
                </Text>
                <Table variant="simple" size="sm" mt={2}>
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th>Pozycja</Th>
                      <Th isNumeric>Wartość netto</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr _hover={{ bg: hoverBg }}>
                      <Td>Materiały</Td>
                      <Td isNumeric fontWeight="medium">
                        {fmtMoney(infoMeldunek?.material_cost_netto)}
                      </Td>
                    </Tr>
                    <Tr _hover={{ bg: hoverBg }}>
                      <Td>Usługi</Td>
                      <Td isNumeric fontWeight="medium">
                        {fmtMoney(infoMeldunek?.service_cost_netto)}
                      </Td>
                    </Tr>
                    <Tr _hover={{ bg: hoverBg }}>
                      <Td fontWeight="bold">Koszt produkcji łącznie</Td>
                      <Td isNumeric fontWeight="bold">
                        {fmtMoney(infoMeldunek?.total_cost_netto)}
                      </Td>
                    </Tr>
                    <Tr _hover={{ bg: hoverBg }}>
                      <Td fontWeight="bold">
                        Koszt jednostkowy produktu końcowego
                      </Td>
                      <Td isNumeric fontWeight="bold" color="blue.500">
                        {fmtMoney(infoMeldunek?.unit_cost_netto)}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>

              {infoMeldunek?.note && (
                <Box>
                  <Text
                    fontWeight="bold"
                    color={mutedTextColor}
                    fontSize="xs"
                    textTransform="uppercase"
                  >
                    Uwagi
                  </Text>
                  <Text mt={1} fontSize="sm">
                    {infoMeldunek.note}
                  </Text>
                </Box>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={() => setInfoMeldunek(null)}>
              Zamknij
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
